import {useCallback, useReducer} from 'react';
import {toast} from 'react-toastify';
import {getGetProjectsQueryKey, useDeleteProjects, useGetProjects} from 'api/generated';
import {queryClient} from 'App';
import {useCheckPlan} from 'hooks';
import {Button, NoItem, Transitioner} from 'components';

import ProjectItem from './Projects.Item';
import ModalProjectCreate from './Modal.Project.Create';

interface IProjectsMineProps {}

const ProjectsMine = ({}: IProjectsMineProps) => {
  const [showModal, toggleModal] = useReducer(s => !s, false);

  const {data: projectsData, isFetching} = useGetProjects({filter: 'all'}, {query: {staleTime: 5 * 1000}});
  const projects = projectsData?.data?.data ?? [];
  const myProjects = projects.filter(p => p.isMine);
  const noProjects = !isFetching && projects.length === 0;

  const deleteProject = useDeleteProjects();
  const handleDelete = useCallback(async (uid: string) => {
    const {data: res} = await deleteProject.mutateAsync({data: {uids: [uid]}});
    if (res.data?.success) {
      toast.success(res.data.message);
      queryClient.invalidateQueries({queryKey: [getGetProjectsQueryKey({filter: 'all'})[0]]});
    } else {
      toast.error('Error deleting project/s');
    }
  }, []);

  const {isLimitReached} = useCheckPlan({
    featureId: 'projects',
    data: myProjects,
    isDataFetching: isFetching,
  });

  return (
    <Transitioner classNames="pb-12">
      <div className="flex items-center justify-between">
        <h1 className="font-medium text-lg dark:text-white">My projects</h1>
        {!noProjects && !isLimitReached && <Button text="+ New project" size="small" onClick={toggleModal} />}
      </div>
      <ModalProjectCreate visible={showModal} onClose={toggleModal} />
      <Transitioner delay={0.1}>
        <ul role="list" className="mt-5 flex flex-col gap-5">
          {noProjects && (
            <NoItem title="No projects" description="Get started by creating a project">
              <div className="flex justify-center items-center mt-3">
                <Button text="+ New project" size="small" onClick={toggleModal} />
              </div>
            </NoItem>
          )}
          {projects.map(project => {
            return <ProjectItem key={project.uid} project={project} onDelete={handleDelete} />;
          })}
        </ul>
      </Transitioner>
    </Transitioner>
  );
};

export default ProjectsMine;
